import { useMemo } from "react";

import TeamChip from "@/components/shared/TeamChip";
import { useTeams } from "@/hooks/useTeams";
import { parsePgArray } from "@/lib/pgArray";

interface TeamBadgeListProps {
  /**
   * The ticket's helper teams. Safe to pass the column straight off any query:
   * parsed here, so a caller reading through a projection that skips
   * normalizeBacklogItem (LinkedItemsSubRow) can't render "{a,b}" as one
   * garbage chip.
   */
  teamIds?: string[] | null | string;
  /** The primary team — the one that owns it. */
  assignedTeamId?: string | null;
  /** Cap the chips rendered, showing a +N counter for the rest. */
  max?: number;
}

/**
 * Renders a ticket's team relationships: the primary team first, then its
 * helpers.
 *
 * This exists so the six read-only ticket renderers each take a single line
 * instead of six hand-written resolve-and-map blocks that would drift. Resolving
 * ids to teams happens here, once, off the cached useTeams query.
 *
 * The primary team is deliberately NOT also drawn as a helper when it appears
 * in both lists: the starred chip already says everything the plain one would,
 * and showing the same team twice reads as a bug.
 */
const TeamBadgeList = ({ teamIds, assignedTeamId, max }: TeamBadgeListProps) => {
  const { data: teams = [] } = useTeams();

  const chips = useMemo(() => {
    if (teams.length === 0) return [];
    const byId = new Map(teams.map((t) => [t.id, t]));

    const primary = assignedTeamId ? byId.get(assignedTeamId) : undefined;
    const helpers = parsePgArray(teamIds)
      .filter((id) => id !== assignedTeamId)
      .map((id) => byId.get(id))
      // A helper whose team was deleted is scrubbed by trigger, so a miss here
      // means the teams query is merely stale — skip it rather than render a
      // blank.
      .filter((t): t is NonNullable<typeof t> => Boolean(t));

    return [
      ...(primary ? [{ team: primary, variant: "primary" as const }] : []),
      ...helpers.map((team) => ({ team, variant: "helper" as const })),
    ];
  }, [teams, teamIds, assignedTeamId]);

  if (chips.length === 0) return null;

  const shown = max ? chips.slice(0, max) : chips;
  const hidden = chips.length - shown.length;

  return (
    <>
      {shown.map(({ team, variant }) => (
        <TeamChip key={team.id} team={team} variant={variant} />
      ))}
      {hidden > 0 && (
        <span
          className="text-[10px] text-muted-foreground font-medium"
          title={chips.map((c) => c.team.name).join(", ")}
        >
          +{hidden}
        </span>
      )}
    </>
  );
};

export default TeamBadgeList;
